import {
  listCompanyDocuments,
  uploadCompanyDocument as uploadCompanyDocumentFile,
  deleteCompanyDocument as removeCompanyDocument,
  setCompanyDocumentStatus,
} from './companyDocuments';
import { http } from '../lib/api/http';

const normalizeCategories = value => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(item => String(item || '').trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};

const mapAdminCompanyRecord = record => {
  if (!record || typeof record !== 'object') {
    return record;
  }
  const categories = normalizeCategories(record.categories || record.category);
  return {
    ...record,
    category: categories,
    categories,
    publicationLimit: record.publication_limit ?? record.publicationLimit ?? null,
    userLimit: record.user_limit ?? record.userLimit ?? null,
    owner: record.owner_id || record.owner || '',
  };
};

const buildAdminCompanyPayload = payload => {
  const body = {};
  const assignString = (key, value) => {
    if (value === undefined || value === null) return;
    const text = String(value || '').trim();
    if (!text) return;
    body[key] = text;
  };

  assignString('name', payload.name);
  assignString('slug', payload.slug);
  assignString('status', payload.status);
  assignString('street', payload.street);
  assignString('street_number', payload.streetNumber);
  assignString('city', payload.city);
  assignString('state', payload.state);
  assignString('country', payload.country);
  assignString('postal_code', payload.postalCode);
  assignString('description', payload.description);

  if (payload.canPublish !== undefined) {
    body.can_publish = Boolean(payload.canPublish);
  }
  if (payload.isVerified !== undefined) {
    body.is_verified = Boolean(payload.isVerified);
  }
  if (payload.publicationLimit !== undefined && payload.publicationLimit !== null) {
    const numericLimit = Number(payload.publicationLimit);
    if (Number.isFinite(numericLimit)) {
      body.publication_limit = Math.max(0, Math.trunc(numericLimit));
    }
  }
  if (payload.userLimit !== undefined && payload.userLimit !== null) {
    const numericLimit = Number(payload.userLimit);
    if (Number.isFinite(numericLimit)) {
      body.user_limit = Math.max(0, Math.trunc(numericLimit));
    }
  }
  if (payload.owner) {
    body.owner_id = payload.owner;
  }

  const categories = normalizeCategories(payload.category ?? payload.categories);
  if (categories.length) {
    body.categories = categories;
  }

  return body;
};

export async function fetchCompanies() {
  const { data } = await http.get('/companies', { params: { status: 'all' } });
  const items = Array.isArray(data?.items) ? data.items : data;
  return (Array.isArray(items) ? items : []).map(mapAdminCompanyRecord);
}

export async function createCompany(payload) {
  const body = buildAdminCompanyPayload(payload);
  const { data } = await http.post('/companies', body);
  return mapAdminCompanyRecord(data);
}

export async function updateCompany(id, payload) {
  if (!id) {
    throw new Error('El identificador de la empresa es obligatorio.');
  }
  const body = buildAdminCompanyPayload(payload);
  const { data } = await http.patch(`/companies/${encodeURIComponent(id)}`, body);
  return mapAdminCompanyRecord(data);
}

export async function deleteCompany(id) {
  if (!id) {
    throw new Error('El identificador de la empresa es obligatorio.');
  }
  await http.delete(`/companies/${encodeURIComponent(id)}`);
  return true;
}

export async function fetchUsers() {
  const { data } = await http.get('/users', { params: { limit: 200 } });
  return Array.isArray(data) ? data : [];
}

export async function createPocketbaseUser(payload) {
  const normalizedEmail = String(payload.email || '').trim().toLowerCase();
  const body = {
    email: normalizedEmail || payload.email,
    password: payload.password,
    display_name: payload.name,
    phone_number: payload.phoneNumber || '',
    role: payload.role || '',
  };

  if (payload.companyId) {
    body.company_id = payload.companyId;
  }

  const { data } = await http.post('/admin/users', body);
  return data;
}

export async function fetchCompanyDocuments(companyId) {
  return listCompanyDocuments(companyId);
}

export async function createCompanyDocument(payload) {
  return uploadCompanyDocumentFile({
    companyId: payload.companyId,
    documentKey: payload.documentKey,
    file: payload.file,
    title: payload.title,
    notes: payload.notes,
  });
}

export async function deleteCompanyDocument(id) {
  return removeCompanyDocument(id);
}

export async function updateCompanyDocumentStatus(id, status) {
  return setCompanyDocumentStatus(id, status);
}
